import React, { useMemo, useState } from 'react';
import { useAuth } from '../context';
import AddressForm from './AddressForm';
import { Link } from 'react-router-dom';
import './AddressBook.css';

// Helpers eeto convert between API/form (snake_case) and internal storage (camelCase)
const toCamel = (v) => ({
  id: v.id,
  fullName: v.full_name ?? v.fullName ?? '',
  line1: v.line1 ?? '',
  line2: v.line2 ?? '',
  city: v.city ?? '',
  state: v.state ?? '',
  postalCode: v.postal_code ?? v.postalCode ?? '',
  country: v.country ?? '',
  phone: v.phone ?? '',
  isDefault: v.is_default ?? v.isDefault ?? false,
});

const toSnake = (v) => ({
  id: v.id,
  full_name: v.fullName ?? v.full_name ?? '',
  line1: v.line1 ?? '',
  line2: v.line2 ?? '',
  city: v.city ?? '',
  state: v.state ?? '',
  postal_code: v.postalCode ?? v.postal_code ?? '',
  country: v.country ?? '',
  phone: v.phone ?? '',
  is_default: v.isDefault ?? v.is_default ?? false,
});

const AddressBook = () => {
  const {
    user,
    getAddresses,
    addAddress,
    updateAddress,
    deleteAddress,
  } = useAuth();
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // address object or null
  const [showForm, setShowForm] = useState(false);

  const addresses = useMemo(() => {
    return getAddresses();
  }, [getAddresses]);

  const initials = useMemo(() => {
    const name = (user?.name && user.name.trim()) || '';
    if (name) {
      const parts = name.split(/\s+/).filter(Boolean);
      const first = parts[0]?.[0] || '';
      const last = parts[parts.length - 1]?.[0] || '';
      return (first + last).toUpperCase();
    }
    const email = user?.email || '';
    return email ? email[0].toUpperCase() : '?';
  }, [user?.name, user?.email]);

  const onCreate = async (data) => {
    await addAddress(data);
    setShowForm(false);
    setEditing(null);
  };

  const onUpdate = async (id, data) => {
    await updateAddress(id, data);
    setShowForm(false);
    setEditing(null);
  };

  const onDelete = async (id) => {
    if (window.confirm('Delete this address?')) {
      try {
        await deleteAddress(id);
      } catch (err) {
        setError(err.message || 'Failed to delete address.');
      }
    }
  };

  const onSetDefault = async (id) => {
    const addr = addresses.find(a => a.id === id);
    if (!addr) return;
    try {
      await updateAddress(id, toCamel({ ...addr, is_default: true }));
    } catch (err) {
      setError(err.message || 'Failed to set default address.');
    }
  };
  // console.log(addresses)

  return (
      <div className="address-book">
          <div className="address-book__header">
              <h1 className="address-book__title">Address Book</h1>
              {error && <div style={{color: '#b00020', marginBottom: 12}}>{error}</div>}
              <div
                className="signed-in-banner"
                aria-label={`Signed in as ${user?.name || user?.email || ''}${user?.name && user?.email ? ` (${user.email})` : ''}`}
              >
                {user?.image_url ? (
                  <img src={user.image_url} alt="" className="sib-avatar" />
                ) : (
                  <div className="sib-avatar" aria-hidden="true">{initials}</div>
                )}
                <div className="sib-text">
                  <span className="sib-label">Signed in as</span>
                  <span className="sib-name">{user?.name || user?.email}</span>
                  {user?.name && user?.email && <span className="sib-email">({user.email})</span>}
                </div>
              </div>
          </div>

          {!showForm && (
              <button
                  className="btn btn-primary"
                  onClick={() => { setError(''); setEditing(null); setShowForm(true); }}
              >
                  Add new address
              </button>
          )}


          {showForm && (
              <div className="address-book__form">
                  <AddressForm
                      initialValues={editing ? toSnake(editing) : {}}
                      onCancel={() => { setShowForm(false); setEditing(null); }}
                      onSubmit={async (values) => {
                          const payload = toSnake({ ...values, id: editing?.id });
                          if (editing?.id) await onUpdate(editing.id, payload);
                          else await onCreate(payload);
                      }}
                  />
              </div>
          )}


          <div className="address-list">
              {addresses.length === 0 && <p className="address-list__empty">No addresses yet. Add one above.</p>}
              <ul className="address-list__grid">
                  {addresses.map(addr => (
                      <li key={addr.id} className="address-card">
                          <div className="address-card__info">
                              <div className="address-card__name">
                                  <strong>{addr.full_name || 'Unnamed'}</strong>
                                  {addr.is_default && <span className="address-card__badge">Default</span>}
                              </div>
                              <div className="address-card__lines">
                                  <div>{addr.line1}</div>
                                  {addr.line2 && <div>{addr.line2}</div>}
                                  <div>{[addr.city, addr.state, addr.postal_code].filter(Boolean).join(', ')}</div>
                                  <div>{addr.country}</div>
                                  {addr.phone && <div className="address-card__phone">Phone: {addr.phone}</div>}
                              </div>
                          </div>
                          <div className="address-card__actions">
                              {!addr.is_default && (
                                  <button className="btn btn-secondary" onClick={() => onSetDefault(addr.id)} title="Set as default">
                                      Set default
                                  </button>
                              )}
                              <button className="btn" onClick={() => { setError(''); setEditing(addr); setShowForm(true); }}>
                                  Edit
                              </button>
                              {!addr.is_default && (
                                  <button className="btn btn-danger" onClick={() => onDelete(addr.id)}>
                                      Delete
                                  </button>
                              )}

                          </div>
                      </li>
                  ))}
              </ul>
          </div>

          <div className="address-book__footer">
              <Link to="/account" className="btn btn-link">Back to Account</Link>
          </div>
      </div>
  );

};

export default AddressBook;
